import { AppPermissions, ViewEditPermissions } from '../../../primary-record-types/abac';
import { Permission, Node, ViewValue, EditValue } from './types';

export const getAllSubPermissions = (node: Node): Permission[] => {
    if (node.type === 'leaf-node') {
        return [node.permission];
    }

    return node.subNodes.reduce((acc, child) => {
        return [...acc, ...getAllSubPermissions(child)];
    }, [] as Permission[]);
};

export const updateSubPermissions = (
    node: Node,
    value: ViewEditPermissions,
    appPermissions: AppPermissions,
): AppPermissions => {
    const copied = { ...appPermissions };
    const allSubPermissions = getAllSubPermissions(node);
    allSubPermissions.forEach((permission) => {
        copied[permission] = value;
    });
    return copied;
};

export const evaluateViewValue = (node: Node, appPermissions: AppPermissions): ViewValue => {
    const allSubPermissions = getAllSubPermissions(node);
    const checkValue = (permission: Permission) =>
        appPermissions[permission] === 'view' || appPermissions[permission] === 'edit';

    const everyValue = allSubPermissions.every(checkValue);
    if (everyValue) {
        return true;
    }

    const someValue = allSubPermissions.some(checkValue);
    if (someValue) {
        return 'maybe';
    }

    return false;
};

export const getShouldDisableViewFromMostRestrictiveAppPermissions = (
    node: Node,
    mostPermissiveAppPermissions: AppPermissions | undefined,
): boolean => {
    if (!mostPermissiveAppPermissions) return false;

    const allSubPermissions = getAllSubPermissions(node);

    const checkValue = (permission: Permission) => mostPermissiveAppPermissions[permission] === 'denied';

    return allSubPermissions.some(checkValue);
};

export const getShouldDisableEditFromMostRestrictiveAppPermissions = (
    node: Node,
    mostPermissiveAppPermissions: AppPermissions | undefined,
): boolean => {
    if (!mostPermissiveAppPermissions) return false;

    const allSubPermissions = getAllSubPermissions(node);
    const checkValue = (permission: Permission) =>
        mostPermissiveAppPermissions[permission] === 'denied' || mostPermissiveAppPermissions[permission] === 'view';

    return allSubPermissions.some(checkValue);
};

export const evaluateEditValue = (node: Node, appPermissions: AppPermissions): EditValue => {
    const viewValue = evaluateViewValue(node, appPermissions);

    if (viewValue === 'maybe' || viewValue === false) {
        return 'disabled';
    }

    const allSubPermissions = getAllSubPermissions(node);
    const checkValue = (permission: Permission) => appPermissions[permission] === 'edit';
    const everyValue = allSubPermissions.every(checkValue);
    if (everyValue) {
        return true;
    } else {
        return false;
    }
};

export const viewEditWidth = 80;
